import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout";
import ProcessReceiveForm from "../../../components/processReceives/ProcessReceiveForm";
import { useAppSelector } from "../../../app/hooks";
import { getLoggingUser } from "../../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../../features/settings/settingsSlice";
import { useCanAccess } from "../../../hooks/useCanAccess";
import { useProcessDefinitionFromQuery } from "../../../hooks/useProcessDefinitionFromQuery";
import { localizedProcessName } from "../../../lib/utils";
import { GET } from "../../../services/axios.service";
import { toastStyles } from "../../../static/styles";
import { route } from "../../../static/route";
import { t } from "../../../locales";
import { ProcessReceiveProps } from "../../../types/processReceive";

const ProcessReceiveEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canAccess = useCanAccess();
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const { processDefinition, loading: definitionLoading } = useProcessDefinitionFromQuery(t.processReceives.processNotSupported);

  const [receive, setReceive] = useState<ProcessReceiveProps | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch && !canAccess("process-receives.update")) {
      navigate(route.dashboard);
    }
  }, [loggingUser, selectedBranch]);

  useEffect(() => {
    const load = async () => {
      try {
        const response: any = await GET(`/v1/process-receives/${id}`, {});
        if (response.data.status === "success") {
          const data = response.data.data;
          if (data && data.can_edit === false) {
            toast.error(t.processReceives.cantEditMessage, { ...(toastStyles as any) });
            navigate(route.dashboard);
            return;
          }
          setReceive(data);
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
          navigate(route.dashboard);
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
        navigate(route.dashboard);
      }
      setLoading(false);
    };
    if (id) load();
  }, [id]);

  const listRoute = processDefinition ? `${route.processReceives}?process=${processDefinition.slug}` : route.dashboard;

  return (
    <Layout>
      <div className="px-2 py-4 md:px-6 md:py-6">
        <nav className="text-sm font-semibold text-gray-500 mb-4">
          <Link to={route.dashboard} className="text-brand-dark hover:underline">
            {t.processReceives.breadcrumbHome}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          {processDefinition && (
            <>
              <Link to={listRoute} className="text-brand-dark hover:underline">
                {t.processReceives.breadcrumbList}
              </Link>
              <span className="mx-2">&rsaquo;</span>
            </>
          )}
          <span className="text-gray-500">{t.processReceives.editTitle}</span>
        </nav>
        <h1 className="text-2xl font-bold text-brand-dark mb-6">
          {processDefinition ? `${localizedProcessName(processDefinition)} — ${t.processReceives.editTitle}` : t.processReceives.editTitle}
        </h1>
        {definitionLoading || !processDefinition || loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#113F67]" />
          </div>
        ) : receive ? (
          <ProcessReceiveForm processDefinition={processDefinition} editReceive={receive} />
        ) : null}
      </div>
    </Layout>
  );
};

export default ProcessReceiveEditPage;
